// NOW — resolve the user's current Step from the data that already exists.
// Resolver order (docs/redesign/DATA_MAP.md §1), first hit is "the" Step and
// the rest become the swipeable route ahead:
//   1 METIS item in its time window         → Execute · METIS
//   2 KYRS task scheduled for now / overdue → Direct  · KYRS
//   3 TKNE Directions step marked Active    → Direct  · TKNE
//   4 METIS next item today                 → Execute · METIS
//   5 KYRS tasks scheduled later today      → Direct  · KYRS
//   6 TKNE next pending step (most drifted) → Direct  · TKNE
//   7 ORACLE Aim with nothing behind it     → Orient  · TELOS
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { parseScheduled } from '../lib/autoSchedule';
import type { Sector } from '../domain/olmps';
import type { UseTodayData } from '../olmps/useTodayData';
import { useOracleProjects, type OracleProject } from '../olmps/useOracleProjects';
import { loadOracleSpine, effectiveAims, type OracleSpine } from '../olmps/oracle-spine-data';
import type { GearId } from '../olmps/ascension';
import { SECTOR_NAME } from './theme';

export type StepSource = 'metis' | 'kyrs' | 'tkne' | 'oracle';

export interface NowStep {
  key: string;
  source: StepSource;
  title: string;
  sector: Sector | null;
  /** Human "when" line, e.g. "Now · until 3:30 PM", "Overdue since 9:00". */
  when: string;
  /** Why this matters right now — one line. */
  why: string;
  /** Endeavour → Goal → Aim context for the breadcrumb, when known. */
  trail: { endeavour?: string | null; goal?: string | null; aim?: string | null; project?: string | null };
  /** ORDER phase this Step lives in. */
  gear: GearId;
  /** Shell tool id to open in context, plus its preselection. */
  tool: string;
  toolOpts?: { initialTaskId?: string };
  /** For TKNE deep-links. */
  designId?: string;
  drift: 'on-pace' | 'behind' | 'lapsed' | null;
  urgent: boolean;
}

export type PaceState = 'fresh' | 'steady' | 'strained' | 'drained';
export interface NowPace {
  charge: number;
  load: number;
  flow: number;
  state: PaceState;
}

/** Raw FLO load (lib/flo.ts priority sum) above which the day counts as heavy;
 *  the book's typical day is ~700. */
const HIGH_LOAD = 700;

function paceOf(flo: UseTodayData['flo']): NowPace | null {
  if (!flo) return null;
  const { charge, load } = flo;
  const state: PaceState =
    charge < 4 ? 'drained' : flo.mode === 'throttled' || load >= HIGH_LOAD ? 'strained' : charge >= 9 ? 'fresh' : 'steady';
  return { charge, load, flow: flo.flow.score, state };
}

interface TaskRow {
  id: string;
  title: string | null;
  scheduled: string | null;
  list_id: string | null;
  sector: string | null;
  design_id: string | null;
}

const asSector = (v: string | null | undefined): Sector | null =>
  v && v.length === 1 && 'SHAPE'.includes(v) ? (v as Sector) : null;

const fmtTime = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

function driftOf(p: OracleProject | undefined): NowStep['drift'] {
  if (!p) return null;
  const worst = Math.max(0, ...p.drift.map((d) => d.score));
  return worst >= 3 ? 'lapsed' : worst >= 1 ? 'behind' : 'on-pace';
}

export function useCurrentStep(userId: string | undefined, today: UseTodayData) {
  const { projects, loading: projLoading, refresh: refreshProjects } = useOracleProjects(userId);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [listSector, setListSector] = useState<Record<string, Sector>>({});
  const [spine, setSpine] = useState<OracleSpine | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const [taskRes, listRes, spineRes] = await Promise.all([
      supabase
        .from('tasks')
        .select('id,title,scheduled,list_id,sector,design_id')
        .eq('user_id', userId)
        .eq('done', false)
        .not('scheduled', 'is', null)
        .limit(400),
      supabase.from('task_lists').select('id,olmps_sector').eq('user_id', userId),
      loadOracleSpine(supabase, userId).catch(() => null),
    ]);
    setTasks(((taskRes.data ?? []) as TaskRow[]).filter((t) => t.title));
    const map: Record<string, Sector> = {};
    for (const l of (listRes.data ?? []) as { id: string; olmps_sector: string | null }[]) {
      if (l.olmps_sector && 'SHAPE'.includes(l.olmps_sector)) map[l.id] = l.olmps_sector as Sector;
    }
    setListSector(map);
    setSpine(spineRes);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const flo = today.flo;
  const pace = useMemo(() => paceOf(flo), [flo]);

  const steps = useMemo<NowStep[]>(() => {
    const now = today.now ?? new Date();
    const out: NowStep[] = [];
    const seen = new Set<string>();
    const push = (s: NowStep) => {
      if (seen.has(s.key)) return;
      seen.add(s.key);
      out.push(s);
    };
    const trailFor = (sector: Sector | null) => {
      const node = sector && spine ? spine[sector] : null;
      const aim = node ? effectiveAims(node)[0]?.label ?? null : null;
      return { endeavour: node?.endeavour ?? null, goal: node?.goal ?? null, aim };
    };
    const allProjects = (Object.values(projects) as OracleProject[][]).flat();
    const taskById = new Map(tasks.map((t) => [t.id, t]));
    // Sector + project for a linked item: own sector first, then the design's
    // sector, then (agenda rows) the linked task's sector / list sector.
    const linkOf = (
      sector: string | null | undefined,
      designId: string | null | undefined,
      taskId?: string | null,
    ) => {
      const proj = designId ? allProjects.find((p) => p.id === designId) : undefined;
      const task = taskId ? taskById.get(taskId) : undefined;
      const sec =
        asSector(sector) ??
        proj?.sector ??
        asSector(task?.sector) ??
        ((task?.list_id && listSector[task.list_id]) || null);
      return { sector: sec, project: proj, designId: designId ?? undefined };
    };
    const trailWith = (sec: Sector | null, project?: OracleProject) =>
      project ? { ...trailFor(sec), project: project.name } : trailFor(sec);

    // 1 · METIS in-window
    const cur = today.currentItem;
    if (cur) {
      const lk = linkOf(cur.sector, cur.designId, cur.taskId);
      push({
        key: `metis:${cur.rowId}:${cur.itemId}`,
        source: 'metis',
        title: cur.text || 'Agenda item',
        sector: lk.sector,
        when: cur.end ? `Now · until ${fmtTime(cur.end)}` : 'Now',
        why: 'It is on your agenda for right now.',
        trail: trailWith(lk.sector, lk.project),
        gear: 'execute',
        tool: 'metis',
        drift: null,
        urgent: true,
      });
    }

    // 2 · KYRS due now / overdue today
    const todayStr = now.toDateString();
    const timed = tasks
      .map((t) => ({ t, at: parseScheduled(t.scheduled) }))
      .filter((x): x is { t: TaskRow; at: Date } => !!x.at);
    const overdue = timed.filter((x) => x.at <= now).sort((a, b) => b.at.getTime() - a.at.getTime());
    for (const { t, at } of overdue.slice(0, 3)) {
      const lk = linkOf(t.sector, t.design_id, t.id);
      const sector = lk.sector;
      const late = at.toDateString() !== todayStr;
      push({
        key: `kyrs:${t.id}`,
        source: 'kyrs',
        title: t.title!,
        sector,
        when: late ? `Overdue since ${at.toLocaleDateString([], { month: 'short', day: 'numeric' })}` : `Due ${fmtTime(at)}`,
        why: sector
          ? `${SECTOR_NAME[sector]} slips while this stays open.`
          : 'Scheduled and still open.',
        trail: trailWith(sector, lk.project),
        gear: 'direct',
        tool: 'kyrs',
        toolOpts: { initialTaskId: t.id },
        drift: late ? 'lapsed' : 'behind',
        urgent: true,
      });
    }

    // 3 · TKNE active steps, most drifted first
    const activeSteps = allProjects
      .flatMap((p) => p.steps.filter((s) => s.status === 'S').map((s) => ({ p, s })))
      .sort((a, b) => Math.max(...b.p.drift.map((d) => d.score)) - Math.max(...a.p.drift.map((d) => d.score)));
    for (const { p, s } of activeSteps.slice(0, 3)) {
      push({
        key: `tkne:${p.id}:${s.id}`,
        source: 'tkne',
        title: s.text || 'Project step',
        sector: p.sector,
        when: 'In progress',
        why: `Moves ${p.name} forward.`,
        trail: { ...trailFor(p.sector), project: p.name },
        gear: 'direct',
        tool: 'tkne',
        designId: p.id,
        drift: driftOf(p),
        urgent: false,
      });
    }

    // 4 · METIS next
    const next = today.nextItem;
    if (next) {
      const lk = linkOf(next.sector, next.designId, next.taskId);
      push({
        key: `metis:${next.rowId}:${next.itemId}`,
        source: 'metis',
        title: next.text || 'Agenda item',
        sector: lk.sector,
        when: next.start ? `Next · ${fmtTime(next.start)}` : 'Next',
        why: 'Next on today’s agenda.',
        trail: trailWith(lk.sector, lk.project),
        gear: 'execute',
        tool: 'metis',
        drift: null,
        urgent: false,
      });
    }

    // 5 · KYRS later today
    const later = timed
      .filter((x) => x.at > now && x.at.toDateString() === todayStr)
      .sort((a, b) => a.at.getTime() - b.at.getTime());
    for (const { t, at } of later.slice(0, 3)) {
      const lk = linkOf(t.sector, t.design_id, t.id);
      const sector = lk.sector;
      push({
        key: `kyrs:${t.id}`,
        source: 'kyrs',
        title: t.title!,
        sector,
        when: `Today · ${fmtTime(at)}`,
        why: 'Scheduled for later today.',
        trail: trailWith(sector, lk.project),
        gear: 'direct',
        tool: 'kyrs',
        toolOpts: { initialTaskId: t.id },
        drift: null,
        urgent: false,
      });
    }

    // 6 · TKNE next pending step of the most drifted projects
    const byDrift = [...allProjects].sort(
      (a, b) => Math.max(...b.drift.map((d) => d.score)) - Math.max(...a.drift.map((d) => d.score)),
    );
    for (const p of byDrift.slice(0, 3)) {
      const s = p.steps.find((x) => x.status === 'P');
      if (!s) continue;
      push({
        key: `tkne:${p.id}:${s.id}`,
        source: 'tkne',
        title: s.text || 'Next project step',
        sector: p.sector,
        when: 'Up next',
        why: driftOf(p) === 'on-pace' ? `Next stage of ${p.name}.` : `${p.name} is behind pace.`,
        trail: { ...trailFor(p.sector), project: p.name },
        gear: 'direct',
        tool: 'tkne',
        designId: p.id,
        drift: driftOf(p),
        urgent: false,
      });
    }

    // 7 · ORACLE aims
    if (spine) {
      for (const sector of Object.keys(spine) as Sector[]) {
        const node = spine[sector];
        const aim = node && effectiveAims(node)[0];
        if (!aim) continue;
        push({
          key: `oracle:${sector}:${aim.id}`,
          source: 'oracle',
          title: aim.label,
          sector,
          when: aim.cadence ? aim.cadence : 'This week',
          why: node.endeavour ? `Your ${SECTOR_NAME[sector]} Aim toward “${node.endeavour}”.` : `Your ${SECTOR_NAME[sector]} Aim.`,
          trail: trailFor(sector),
          gear: 'orient',
          tool: 'telos',
          drift: null,
          urgent: false,
        });
      }
    }
    // NUMA pace: when Load is high, the chosen Step says why it is the one.
    if (out[0] && pace && pace.load >= HIGH_LOAD) {
      out[0] = { ...out[0], why: 'Load is high — this is the one that moves the Aim.' };
    }
    return out;
  }, [today.now, today.currentItem, today.nextItem, tasks, listSector, projects, spine, pace]);

  return {
    steps,
    pace,
    loading: loading || projLoading,
    projects,
    spine,
    refresh: () => {
      void load();
      refreshProjects();
      today.refresh();
    },
  };
}
